// Utility functions for Funds Management components

const CURRENCY_SYMBOL_MAP = {
  USD: '$', EUR: '€', GBP: '£', AUD: 'A$', JPY: '¥',
  CAD: 'C$', CHF: 'CHF', NZD: 'NZ$', SGD: 'S$'
};

export const getCurrencySymbol = (code) => CURRENCY_SYMBOL_MAP[code] || code || '';

export const formatCurrency = (amount, currency) => {
  const code = currency || 'USD';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  } catch {
    // Fallback for unsupported currency codes
    return `${getCurrencySymbol(code)}${Number(amount || 0).toFixed(2)}`;
  }
};

export const formatCompactCurrency = (amount, currency) => {
  const n = Number(amount);
  if (isNaN(n)) return '0';
  const abs = Math.abs(n);
  const sym = getCurrencySymbol(currency);
  if (abs >= 1000000) return `${sym}${(abs / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `${sym}${(abs / 1000).toFixed(1)}K`;
  return `${sym}${abs.toFixed(0)}`;
};

export const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString();
};

export const getTransactionTypeColor = (type) => {
  switch (type?.toLowerCase()) {
    case 'deposit':
    case 'add':
      return 'success';
    case 'withdrawal':
    case 'remove':
      return 'danger';
    case 'adjustment':
      return 'warning';
    default:
      return 'info';
  }
};

export const extractTransactionsData = (apiResponse) => {
  if (!apiResponse) return [];
  
  // Handle the specific nested structure from the API response: success.data.data.data
  if (apiResponse.success && apiResponse.data && apiResponse.data.data && Array.isArray(apiResponse.data.data.data)) {
    return apiResponse.data.data.data;
  }
  
  // Handle new standardized API response format
  if (apiResponse.success && apiResponse.data) {
    if (Array.isArray(apiResponse.data.transactions)) {
      return apiResponse.data.transactions;
    } else if (Array.isArray(apiResponse.data)) {
      return apiResponse.data;
    }
  }
  
  // Handle different API response structures (backward compatibility)
  if (apiResponse.data && Array.isArray(apiResponse.data.data)) {
    return apiResponse.data.data;
  } else if (apiResponse.data && Array.isArray(apiResponse.data)) {
    return apiResponse.data;
  } else if (Array.isArray(apiResponse.data)) {
    return apiResponse.data;
  } else if (Array.isArray(apiResponse)) {
    return apiResponse;
  } else {
    return [];
  }
};

export const extractBalanceData = (apiResponse) => {
  if (!apiResponse) return null;
  
  // Handle new standardized API response format
  if (apiResponse.success && apiResponse.data) {
    // Check for nested structure: data.data.user (from user transactions API)
    if (apiResponse.data.data && apiResponse.data.data.user) {
      return apiResponse.data.data.user;
    }
    // Check for direct user object in data
    else if (apiResponse.data.user) {
      return apiResponse.data.user;
    }
    // Return data directly if it contains balance info
    else {
      return apiResponse.data;
    }
  }
  
  // Handle different API response structures (backward compatibility)
  if (apiResponse.data && apiResponse.data.data) {
    // Check if the nested data contains user info
    if (apiResponse.data.data.user) {
      return apiResponse.data.data.user;
    }
    return apiResponse.data.data;
  } else if (apiResponse.data) {
    return apiResponse.data;
  } else {
    return apiResponse;
  }
};